<script setup>
import { computed, ref, onMounted, watch, nextTick, toRef } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import AiAssistant from 'dashboard/components-next/aiAssistant/AiAssistant.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const currentChat = toRef(props, 'currentChat');

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const activeTab = computed(() => {
  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_ai_assistant_open: isAiAssistantOpen,
  } = uiSettings.value;

  if (isContactSidebarOpen) return 'contact';
  if (isAiAssistantOpen) return 'ai_assistant';
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeSidebar = () => {
  if (
    isSmallScreen.value &&
    (uiSettings.value?.is_contact_sidebar_open ||
      uiSettings.value?.is_ai_assistant_open)
  ) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
      is_ai_assistant_open: false,
    });
  }
};

const lastCustomerMessage = computed(() => {
  const messages = currentChat.value?.messages || [];
  const customerMessages = messages.filter(
    message => message.message_type === 0
  );
  return customerMessages[customerMessages.length - 1]?.content || '';
});

const pendingCustomerMessages = computed(() => {
  const messages = currentChat.value?.messages || [];
  const stack = [];

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.message_type === 0) {
      stack.unshift(message.content || '');
    } else {
      break;
    }
  }

  return stack;
});

const isLastMessageCustomer = computed(() => {
  const messages = currentChat.value?.messages || [];
  if (!messages.length) return false;
  const last = messages[messages.length - 1];
  return last.message_type === 0;
});

const aiProvider = ref(null); // 'ailab' | null
const ailabAssistantId = ref(null); // ailab assistant id (ai closer/gamer)
const providerResolved = ref(false); // avoid UI flipping before provider is known

const fetchAiAssistantProvider = async () => {
  try {
    providerResolved.value = false;
    const conversationId = currentChat.value?.id;
    if (!conversationId) {
      ailabAssistantId.value = null;
      aiProvider.value = null;
      providerResolved.value = true;
      return;
    }

    const response = await ConversationApi.getInboxAssistant(conversationId);
    const assistant = response?.data?.assistant;

    const sidebarKey = assistant?.wlab_sidebar_key;
    const hasAilab =
      Boolean(assistant?.id) &&
      ['ai_closer', 'ai_gamer', 'ai_studio'].includes(
        (sidebarKey || 'ai_closer').toString()
      );

    const forcedProvider = (
      uiSettings.value?.ai_assistant_provider || ''
    ).toString();

    const syncProviderSetting = provider => {
      const current = (
        uiSettings.value?.ai_assistant_provider || ''
      ).toString();
      if (provider && current !== provider) {
        updateUISettings({ ai_assistant_provider: provider });
      }
    };

    if (forcedProvider === 'ailab' && hasAilab) {
      aiProvider.value = 'ailab';
      ailabAssistantId.value = assistant.id;
      syncProviderSetting('ailab');
      providerResolved.value = true;
      return;
    }

    if (hasAilab) {
      aiProvider.value = 'ailab';
      ailabAssistantId.value = assistant.id;
      syncProviderSetting('ailab');
      providerResolved.value = true;
      return;
    }

    aiProvider.value = null;
    ailabAssistantId.value = null;
    providerResolved.value = true;
  } catch (error) {
    ailabAssistantId.value = null;
    aiProvider.value = null;
    providerResolved.value = true;
  }
};

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !uiSettings.value?.is_contact_sidebar_open,
    is_copilot_panel_open: false,
    is_ai_assistant_open: false,
  });
};

const hasAnyAiAssistant = computed(() => {
  return aiProvider.value === 'ailab' && Boolean(ailabAssistantId.value);
});

watch([hasAnyAiAssistant, activeTab], async ([hasAi, tab]) => {
  // Important: avoid flipping the UI before we've resolved provider/bindings.
  // Otherwise clicking the AI icon can appear "unresponsive" (panel immediately forced back).
  if (tab === 'ai_assistant' && providerResolved.value && !hasAi) {
    // Re-resolve once to avoid stale state/race conditions.
    await fetchAiAssistantProvider();
    await nextTick();
    if (hasAnyAiAssistant.value) return;
    updateUISettings({
      is_ai_assistant_open: false,
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
    await nextTick();
    updateUISettings({
      is_ai_assistant_open: false,
      is_contact_sidebar_open: true,
      is_copilot_panel_open: false,
    });
    await nextTick();
  }
});

onMounted(() => {
  fetchAiAssistantProvider();
});

watch(
  () => activeTab.value,
  newTab => {
    if (newTab === 'ai_assistant') {
      fetchAiAssistantProvider();
    }
  }
);

// If the panel is opened directly (icon click), force a provider refresh.
watch(
  () => uiSettings.value?.is_ai_assistant_open,
  open => {
    if (open) fetchAiAssistantProvider();
  }
);

// When switching between Bridge/AiLab buttons while the panel is open, refresh provider.
watch(
  () => uiSettings.value?.ai_assistant_provider,
  () => {
    if (activeTab.value === 'ai_assistant') {
      fetchAiAssistantProvider();
    }
  }
);

watch(
  () => currentChat.value?.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      providerResolved.value = false;
      const inboxId = currentChat.value?.inbox_id;
      if (inboxId) {
        try {
          await fetchAiAssistantProvider();
        } catch (error) {
          if (activeTab.value === 'ai_assistant') {
            ailabAssistantId.value = null;
            aiProvider.value = null;
            updateUISettings({
              is_ai_assistant_open: false,
              is_contact_sidebar_open: true,
            });
          }
        }
      }
    }
  }
);

const handleInsertReply = text => {
  document.dispatchEvent(
    new CustomEvent('insert-ai-reply', { detail: { text } })
  );
};

const handleSendReply = text => {
  document.dispatchEvent(
    new CustomEvent('send-ai-reply', { detail: { text } })
  );
};
</script>

<template>
  <!-- Outer wrapper with #080808 background and left gutter -->
  <div
    v-on-click-outside="() => closeSidebar()"
    class="conversation-sidebar-wrapper h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-none transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 ltr:pl-0 rtl:pr-0 md:static md:w-[340px] md:min-w-[340px] md:ltr:pl-[6px] md:rtl:pr-[6px] 2xl:min-w-[380px] 2xl:w-[380px] max-[1600px]:w-[300px] max-[1600px]:min-w-[300px] shadow-lg md:shadow-none"
    style="background-color: var(--wlab-bg-sidebar)"
    :class="[
      {
        'md:flex': activeTab !== null,
        'md:hidden': activeTab === null,
      },
    ]"
  >
    <!-- Inner container with rounded corners -->
    <div
      class="conversation-sidebar-panel flex flex-1 overflow-auto rounded-none md:rounded-2xl relative"
      style="background-color: var(--wlab-bg-primary)"
    >
      <div
        v-if="hasAnyAiAssistant"
        v-show="activeTab === 'ai_assistant'"
        class="absolute inset-0 z-10"
      >
        <AiAssistant
          :conversation-id="currentChat.id"
          :assistant-id="ailabAssistantId"
          :last-customer-message="lastCustomerMessage"
          :pending-customer-messages="pendingCustomerMessages"
          :is-last-message-customer="isLastMessageCustomer"
          @insert-reply="handleInsertReply"
          @send-reply="handleSendReply"
        />
      </div>
      <div
        v-show="activeTab !== 'ai_assistant' || !hasAnyAiAssistant"
        class="w-full h-full contact-panel-outer"
        style="padding: 0"
      >
        <ContactPanel
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 768px) {
  .conversation-sidebar-wrapper {
    left: 0 !important;
    right: 0 !important;
    width: 100vw !important;
    min-width: 100vw !important;
    max-width: 100vw !important;
    transform-origin: right center;
    box-shadow: 0 20px 44px rgba(0, 0, 0, 0.28);
  }

  .conversation-sidebar-panel {
    transition:
      border-radius 260ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.04),
      0 0 0 1px rgba(17, 24, 39, 0.04);
  }
}
</style>
